import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../brand/useBrand';

/**
 * SEO Component - Add to any page for optimized meta tags
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="Page description"
 *   keywords="keyword1, keyword2"
 *   image="https://homesbyli.com/og-image.jpg"
 *   url="https://homesbyli.com/page-path"
 * />
 */
const LANG_TO_OG_LOCALE = {
  en: 'en_US',
  fr: 'fr_CA',
  es: 'es_ES',
  zh: 'zh_CN',
  ar: 'ar_AR',
};

const SEO = ({
  title,
  description,
  keywords = 'Montreal rentals, short-term rentals, Montreal Airbnb, vacation rentals Montreal, Montreal apartments, Montreal accommodation, Experience Montreal',
  image,
  url,
  type = 'website',
  author,
}) => {
  const { i18n } = useTranslation();
  const { brand } = useBrand();
  const seo = brand?.seo || {};
  const brandName = brand?.displayName || 'Homesbyli';
  const lang = (i18n.language || 'en').split('-')[0];
  const ogLocale = LANG_TO_OG_LOCALE[lang] || 'en_US';
  // Brand-aware defaults: pages that pass nothing inherit the active
  // brand's identity (UCWonder on ucwonder.ai, Homesbyli on homesbyli.com).
  const resolvedTitle = title || seo.title || `${brandName}`;
  const resolvedDescription = description || seo.description || '';
  const resolvedImage = image || seo.ogImage || 'https://homesbyli.com/og-image.jpg';
  const resolvedUrl = url
    || `${seo.canonicalOrigin || 'https://homesbyli.com'}${typeof window !== 'undefined' ? window.location.pathname : ''}`;
  const resolvedAuthor = author || brandName;
  const fullTitle = resolvedTitle.includes(brandName) ? resolvedTitle : `${resolvedTitle} | ${brandName}`;

  return (
    <Helmet
      title={fullTitle}
      htmlAttributes={{ lang, dir: lang === 'ar' ? 'rtl' : 'ltr' }}
    >
      {/* Primary Meta Tags */}
      <meta name="title" content={fullTitle} />
      <meta name="description" content={resolvedDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={resolvedAuthor} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content={lang} />
      <meta name="revisit-after" content="7 days" />

      {/* Canonical URL */}
      <link rel="canonical" href={resolvedUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={resolvedUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:site_name" content={brandName} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={resolvedUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      {/* Geo Tags for Montreal */}
      <meta name="geo.region" content="CA-QC" />
      <meta name="geo.placename" content="Montreal" />
      <meta name="geo.position" content="45.5017;-73.5673" />
      <meta name="ICBM" content="45.5017, -73.5673" />

      {/* Additional SEO */}
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
    </Helmet>
  );
};

export default SEO;
